import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { format, addMonths, startOfMonth, addDays, addMinutes } from "date-fns";
import { InvoiceHeader } from "./form-sections/InvoiceHeader";
import { SellerInformation } from "./form-sections/SellerInformation";
import { BuyerInformation } from "./form-sections/BuyerInformation";
import { InvoiceLineItems } from "./form-sections/InvoiceLineItems";
import { formSchema, type FormData } from "./types";
import { useInvoiceSubmit } from "./hooks/useInvoiceSubmit";
import { Loader2, ChevronDown, ChevronUp, Lock, X } from "lucide-react";
import { ReminderStageSection } from "./schedule/ReminderStageSection";
import { ReminderStage } from "./schedule/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { combineDateTime } from "@/utils/invoiceUtils";
import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";

interface CreateInvoiceFormProps {
  mode: "create" | "edit" | "schedule";
  invoiceIds?: string[];
  existingInvoice?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
  initialDetailsExpanded?: boolean;
  initialScheduleExpanded?: boolean;
  defaultRecipientEmail?: string;
}

export const CreateInvoiceForm = ({
  mode = "create",
  invoiceIds = [],
  existingInvoice,
  onSuccess,
  onCancel,
  initialDetailsExpanded = false,
  initialScheduleExpanded = false,
  defaultRecipientEmail = "",
}: CreateInvoiceFormProps) => {
  const navigate = useNavigate();
  const today = new Date();
  const nextMonthStart = startOfMonth(addMonths(today, 1));
  const { onSubmit } = useInvoiceSubmit(onSuccess);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScheduleExpanded, setIsScheduleExpanded] = useState(initialScheduleExpanded);
  const [recipientEmails, setRecipientEmails] = useState<string[]>(defaultRecipientEmail ? [defaultRecipientEmail] : []);
  const [currentEmail, setCurrentEmail] = useState("");
  const [createPaymentLink, setCreatePaymentLink] = useState(false);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [initialStage, setInitialStage] = useState<ReminderStage>({
    date: undefined,
    time: (() => {
      // Get current time + 15 minutes
      const now = new Date();
      const futureTimeRaw = addMinutes(now, 15);

      // Round to nearest 5 minutes
      const minutes = futureTimeRaw.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;

      // Create a new date with rounded minutes
      const futureTime = new Date(futureTimeRaw);
      futureTime.setMinutes(roundedMinutes % 60);

      // If we rounded to 60, add an hour
      if (roundedMinutes === 60) {
        futureTime.setHours(futureTime.getHours() + 1);
      }

      return format(futureTime, 'HH:mm');
    })(),
    templateId: undefined
  });

  const [firstReminder, setFirstReminder] = useState<ReminderStage>({
    date: undefined,
    time: '09:00',
    templateId: undefined
  });

  const [secondReminder, setSecondReminder] = useState<ReminderStage>({
    date: undefined,
    time: '09:00',
    templateId: undefined
  });

  const [finalReminder, setFinalReminder] = useState<ReminderStage>({
    date: undefined,
    time: '09:00',
    templateId: undefined
  });

  const { data: subscriptionInfo } = useQuery({
    queryKey: ["subscription-info"],
    queryFn: async () => {
      const userId = (await supabase.auth.getUser()).data.user?.id;

      const { data: profile, error: profileError } = await supabase
        .from('business_profiles')
        .select('subscription_tier, stripe_charges_enabled, stripe_details_submitted, sepa_enabled')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        console.error("Error fetching subscription tier:", profileError);
        return null;
      }

      const stripeEnabled = profile.stripe_charges_enabled;
      const sepaEnabled = profile.sepa_enabled;

      // Only set payment link checkbox based on Stripe configuration for new invoices
      // and only if an initial date has been selected (scheduling is happening)
      if (mode !== "edit") {
        setCreatePaymentLink(stripeEnabled && Boolean(initialStage.date));
      }

      const { data: limits, error: limitsError } = await supabase
        .from('subscription_limits')
        .select('*')
        .eq('tier', profile.subscription_tier)
        .single();

      if (limitsError) {
        console.error("Error fetching subscription limits:", limitsError);
        return null;
      }

      const { count: currentCount, error: countError } = await supabase
        .from('invoice_schedules')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', new Date(new Date().setDate(1)).toISOString())
        .lt('created_at', new Date(new Date().setMonth(new Date().getMonth() + 1, 1)).toISOString());

      if (countError) {
        console.error("Error counting schedules:", countError);
        return null;
      }

      return {
        tier: profile.subscription_tier,
        limit: limits.monthly_schedule_limit,
        used: currentCount,
        canSchedule: (currentCount ?? 0) < limits.monthly_schedule_limit,
        stripeEnabled: stripeEnabled,
        sepaEnabled: sepaEnabled
      };
    }
  });

  const defaultValues = existingInvoice ? {
    invoiceNumber: existingInvoice.invoice_number,
    issueDate: new Date(existingInvoice.issue_date),
    dueDate: new Date(existingInvoice.due_date),
    businessName: existingInvoice.business_name,
    businessTaxId: existingInvoice.business_tax_id,
    businessIban: existingInvoice.business_iban,
    businessSwift: existingInvoice.business_swift,
    businessEmail: existingInvoice.business_email,
    businessPhone: existingInvoice.business_phone,
    recipientName: existingInvoice.recipient_name,
    recipientEmail: existingInvoice.recipient_email,
    recipientAddress: existingInvoice.recipient_address,
    clientTaxId: existingInvoice.client_tax_id,
    clientPhone: existingInvoice.client_phone,
    lineItems: existingInvoice.lineItems || [{
      description: existingInvoice.description,
      quantity: existingInvoice.quantity,
      unitPrice: existingInvoice.unit_price,
    }],
    bankName: existingInvoice.bank_name,
    bankAccountHolder: existingInvoice.bank_account_holder,
    bankIban: existingInvoice.bank_iban,
    bankSwift: existingInvoice.bank_swift,
    paymentReference: existingInvoice.payment_reference,
    taxes: existingInvoice.taxes || [],
    recipients_emails: [],
    currency: existingInvoice.currency || "EUR",
  } : {
    invoiceNumber: "",
    issueDate: today,
    dueDate: nextMonthStart,
    businessName: "",
    businessTaxId: "",
    businessIban: "",
    businessSwift: "",
    businessEmail: "",
    businessPhone: "",
    recipientName: "",
    recipientEmail: "",
    recipientAddress: "",
    clientTaxId: "",
    clientPhone: "",
    lineItems: [
      {
        description: "",
        quantity: 1,
        unitPrice: 0,
      }
    ],
    bankName: "",
    bankAccountHolder: "",
    bankIban: "",
    bankSwift: "",
    paymentReference: "",
    taxes: [],
    recipients_emails: [],
    currency: "EUR",
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues
  });

  useEffect(() => {
    if (existingInvoice) {
      const loadScheduleData = async () => {
        const { data: schedule } = await supabase
          .from('invoice_schedules')
          .select('*')
          .eq('invoice_id', existingInvoice.id)
          .single();

        if (schedule) {
          setInitialStage({
            date: new Date(schedule.initial_date),
            time: format(new Date(schedule.initial_date), 'HH:mm'),
            templateId: schedule.initial_template_id
          });

          if (schedule.first_reminder_date) {
            setFirstReminder({
              date: new Date(schedule.first_reminder_date),
              time: format(new Date(schedule.first_reminder_date), 'HH:mm'),
              templateId: schedule.first_reminder_template_id
            });
          }

          if (schedule.second_reminder_date) {
            setSecondReminder({
              date: new Date(schedule.second_reminder_date),
              time: format(new Date(schedule.second_reminder_date), 'HH:mm'),
              templateId: schedule.second_reminder_template_id
            });
          }

          if (schedule.final_reminder_date) {
            setFinalReminder({
              date: new Date(schedule.final_reminder_date),
              time: format(new Date(schedule.final_reminder_date), 'HH:mm'),
              templateId: schedule.final_reminder_template_id
            });
          }
        }
      };

      loadScheduleData();
    }
  }, [existingInvoice]);

  useEffect(() => {
    if (mode === "edit" && existingInvoice?.recipients_emails) {
      try {
        const emails = Array.isArray(existingInvoice.recipients_emails)
          ? existingInvoice.recipients_emails
          : typeof existingInvoice.recipients_emails === 'string'
            ? existingInvoice.recipients_emails
              .replace(/^\{|\}$/g, '') // Remove PostgreSQL array formatting if present
              .split(',')
              .map(email => email.trim())
            : [];

        setRecipientEmails(emails.filter(Boolean));
      } catch (error) {
        console.error('Error parsing recipients_emails:', error);
        setRecipientEmails([]);
      }
    }
  }, [mode, existingInvoice]);

  const issueDate = form.watch("issueDate");
  const dueDate = form.watch("dueDate");
  const businessName = form.watch("businessName");
  const businessIban = form.watch("businessIban");
  const businessSwift = form.watch("businessSwift");
  const buyerEmail = form.watch("recipientEmail");

  useEffect(() => {
    if (mode !== "edit" && buyerEmail && !recipientEmails.includes(buyerEmail)) {
      setRecipientEmails(prev => [...prev, buyerEmail]);
    }
  }, [buyerEmail, recipientEmails, mode]);

  useEffect(() => {
    if (dueDate && issueDate && dueDate <= issueDate) {
      const nextDay = new Date(issueDate);
      nextDay.setDate(nextDay.getDate() + 1);
      form.setValue("dueDate", nextDay);
    }
  }, [issueDate, dueDate, form]);

  useEffect(() => {
    form.setValue("bankAccountHolder", businessName);
    form.setValue("bankIban", businessIban);
    form.setValue("bankSwift", businessSwift);
  }, [businessName, businessIban, businessSwift, form]);

  useEffect(() => {
    form.setValue('recipients_emails', recipientEmails);
  }, [recipientEmails, form]);

  const calculateReminderDates = (initialDate: Date | undefined, dueDate: Date | undefined) => {
    if (!initialDate || !dueDate) return;

    // Calculate the total duration between initial date and due date
    const totalDays = Math.max(1, Math.round((dueDate.getTime() - initialDate.getTime()) / (1000 * 60 * 60 * 24)));

    // First reminder: halfway between initial date and due date
    const firstReminderDate = new Date(initialDate.getTime());
    firstReminderDate.setDate(initialDate.getDate() + Math.floor(totalDays / 2));

    // Second reminder: 3 days before due date
    const secondReminderDate = new Date(dueDate.getTime());
    secondReminderDate.setDate(dueDate.getDate() - 3);

    // Final reminder: 2 days after due date
    const finalReminderDate = new Date(dueDate.getTime());
    finalReminderDate.setDate(dueDate.getDate() + 2);

    // Ensure the dates are in chronological order
    if (firstReminderDate > secondReminderDate) {
      firstReminderDate.setTime(initialDate.getTime() + (secondReminderDate.getTime() - initialDate.getTime()) / 2);
    }

    setFirstReminder(prev => ({ ...prev, date: firstReminderDate }));
    setSecondReminder(prev => ({ ...prev, date: secondReminderDate }));
    setFinalReminder(prev => ({ ...prev, date: finalReminderDate }));
  };

  const handleInitialDateChange = (date: Date) => {
    setInitialStage(prev => ({ ...prev, date }));

    // Get the due date from the form
    const dueDate = form.getValues("dueDate");

    if (dueDate) {
      calculateReminderDates(date, dueDate);
    } else {
      // Fallback to the original logic if due date is not available
      setFirstReminder(prev => ({ ...prev, date: addDays(date, 14) }));
      setSecondReminder(prev => ({ ...prev, date: addDays(date, 21) }));
      setFinalReminder(prev => ({ ...prev, date: addDays(date, 30) }));
    }
  };

  useEffect(() => {
    if (dueDate && initialStage.date) {
      calculateReminderDates(initialStage.date, dueDate);
    }
  }, [dueDate]);

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.endsWith(",")) {
      const email = value.slice(0, -1).trim();
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (!recipientEmails.includes(email)) {
          setRecipientEmails(prev => [...prev, email]);
        }
        setCurrentEmail("");
      }
    } else {
      setCurrentEmail(value);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const email = currentEmail.trim();
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (!recipientEmails.includes(email)) {
          setRecipientEmails(prev => [...prev, email]);
        }
        setCurrentEmail("");
      }
    } else if (e.key === "Backspace" && currentEmail === "" && recipientEmails.length > 0) {
      setRecipientEmails(prev => prev.slice(0, -1));
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setRecipientEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleScheduleSubmit = async (invoiceId: string) => {
    if (!initialStage.date || !initialStage.templateId) {
      toast.error(t("toasts.set_initial_date"));
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("toasts.login_to_schedule"));
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('business_profiles')
        .select('stripe_account_id, stripe_charges_enabled, stripe_details_submitted, sepa_enabled')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile) {
        console.error('Error fetching business profile:', profileError);
        throw new Error('Business profile not found');
      }

      const initialDateTime = combineDateTime(initialStage.date, initialStage.time);

      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('subtotal, amount, bank_iban, recipient_email, recipients_emails, recipient_address, description')
        .eq('id', invoiceId)
        .single();

      if (invoiceError || !invoice) {
        console.error('Error fetching invoice:', invoiceError);
        throw new Error('Invoice not found');
      }

      const emailsToUse = (invoice.recipients_emails && invoice.recipients_emails.length > 0)
        ? invoice.recipients_emails
        : recipientEmails.length > 0
          ? recipientEmails
          : [invoice.recipient_email];

      // Only create payment links if the checkbox is checked
      let paymentUrl = null;
      let paymentSessionId = null;

      if (createPaymentLink) {
        // Check if Stripe is properly configured
        if (!profile.stripe_account_id || !profile.stripe_charges_enabled) {
          toast.error(
            <div className="flex flex-col gap-2">
              <p className="text-sm font-thin">
                {t("toasts.failed_create_payment_link")}{" "}
                <a
                  href="/account_settings"
                  className="text-primary hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/account_settings");
                  }}
                >
                  {t("toasts.connect_stripe_account")}
                </a>
              </p>
            </div>
          );
          return;
        }

        // const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-checkout-link', {
        //   body: {
        //     amount: invoice.amount,
        //     invoice_id: invoiceId,
        //     currency: 'eur',
        //     recipient_email: invoice.recipient_email
        //   }
        // });

        // if (checkoutError) {
        //   console.error('Error creating checkout link:', checkoutError);
        //   throw checkoutError;
        // }

        // console.log('checkoutData', checkoutData);



        const { data: paymentData, error: paymentError } = await supabase.functions.invoke('create-checkout-link', {
          body: {
            amount: invoice.amount,
            invoice_id: invoiceId,
            currency: 'eur',
            recipient_email: invoice.recipient_email
          }
        });

        if (paymentError) {
          console.error('Error creating payment link:', paymentError);
          if (paymentError.message?.includes("No Stripe account found")) {
            toast.error(
              <div className="flex flex-col gap-2">
                <p>{t("toasts.setup_stripe_first")}</p>
                <a
                  href="/account_settings"
                  className="text-primary hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/account_settings");
                  }}
                >
                  {t("toasts.go_to_account_settings")}
                </a>
              </div>
            );
            return;
          }
          throw paymentError;
        }

        if (paymentData?.url) {
          paymentUrl = paymentData.url;
          paymentSessionId = paymentData.id;
        }
      }

      // Update invoice with payment info (if available) and status
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          payment_url: paymentUrl,
          payment_session_id: paymentSessionId,
          status: 'scheduled',
          recipients_emails: emailsToUse
        })
        .eq('id', invoiceId);

      if (updateError) throw updateError;

      if (mode === "edit") {
        const { error: deleteError } = await supabase
          .from('invoice_schedules')
          .delete()
          .eq('invoice_id', invoiceId);

        if (deleteError) throw deleteError;
      }

      const { error: scheduleError } = await supabase
        .from('invoice_schedules')
        .insert({
          invoice_id: invoiceId,
          user_id: user.id,
          recipient_email: emailsToUse.join(','),
          recipients_emails: emailsToUse,
          initial_date: initialDateTime.toISOString(),
          initial_template_id: initialStage.templateId,
          first_reminder_template_id: firstReminder.templateId,
          first_reminder_date: firstReminder.date ? combineDateTime(firstReminder.date, firstReminder.time).toISOString() : null,
          second_reminder_template_id: secondReminder.templateId,
          second_reminder_date: secondReminder.date ? combineDateTime(secondReminder.date, secondReminder.time).toISOString() : null,
          final_reminder_template_id: finalReminder.templateId,
          final_reminder_date: finalReminder.date ? combineDateTime(finalReminder.date, finalReminder.time).toISOString() : null,
        });

      if (scheduleError) {
        console.error('Error creating schedule:', scheduleError);
        throw scheduleError;
      }

      toast.success(mode === "edit"
        ? t("toasts.invoice_updated_and_scheduled")
        : t("toasts.invoice_created_and_scheduled"));

      onSuccess?.();
    } catch (error: any) {
      console.error('Error scheduling invoice:', error);
      toast.error(t("toasts.failed_schedule_invoice"));
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      // For bulk scheduling mode, use the dedicated function
      if (mode === "schedule" && invoiceIds.length > 0) {
        await handleBulkScheduleSubmit();
        return;
      }

      // Calculate the new amount from the form data
      const subtotal = data.lineItems.reduce((total, item) => {
        return total + (item.quantity * item.unitPrice);
      }, 0);

      const totalTaxAmount = data.taxes?.reduce((total, tax) => {
        return total + (subtotal * tax.percentage / 100);
      }, 0) || 0;

      const newAmount = subtotal + totalTaxAmount;

      // Get the original invoice amount if we're in edit mode
      let originalAmount = 0;
      if (mode === "edit" && existingInvoice) {
        originalAmount = existingInvoice.amount || 0;
      }

      // Submit the form data to create/update the invoice
      const { invoiceId } = await onSubmit(
        data,
        mode,
        mode === "edit" && existingInvoice ? existingInvoice.id : undefined
      );

      // Determine if we need to create a new payment link
      const amountChanged = mode === "edit" && newAmount !== originalAmount;
      const needsNewPaymentLink = createPaymentLink && (mode === "edit" || amountChanged);

      // If we're scheduling or the amount changed and payment link is checked, handle payment link
      if (initialStage.date || needsNewPaymentLink) {
        // If we're scheduling, use handleScheduleSubmit which already handles payment links
        if (initialStage.date) {
          await handleScheduleSubmit(invoiceId);
        }
        // Otherwise, just create a new payment link if needed
        else if (needsNewPaymentLink) {
          await createNewPaymentLink(invoiceId, newAmount);
        }
      }

      toast.success(mode === "edit"
        ? t("toasts.invoice_updated")
        : t("toasts.invoice_created"));
      onSuccess?.();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(t("toasts.failed_create_invoice"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // New function to create a payment link
  const createNewPaymentLink = async (invoiceId: string, amount: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("toasts.login_required"));
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('business_profiles')
        .select('stripe_account_id, stripe_charges_enabled, stripe_details_submitted, sepa_enabled')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profile) {
        console.error('Error fetching business profile:', profileError);
        throw new Error('Business profile not found');
      }

      // Check if Stripe is properly configured
      if (!profile.stripe_account_id || !profile.stripe_charges_enabled) {
        toast.error(
          <div className="flex flex-col gap-2">
            <p className="text-sm font-thin">
              {t("toasts.failed_create_payment_link")}{" "}
              <a
                href="/account_settings"
                className="text-primary hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/account_settings");
                }}
              >
                {t("toasts.connect_stripe_account")}
              </a>
            </p>
          </div>
        );
        return;
      }

      // Get the invoice details
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('recipient_email')
        .eq('id', invoiceId)
        .single();

      if (invoiceError || !invoice) {
        console.error('Error fetching invoice:', invoiceError);
        throw new Error('Invoice not found');
      }

      // Create a new payment link
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke('create-checkout-link', {
        body: {
          amount: amount,
          invoice_id: invoiceId,
          currency: 'eur',
          recipient_email: invoice.recipient_email
        }
      });

      if (paymentError) {
        console.error('Error creating payment link:', paymentError);
        throw paymentError;
      }

      // Update the invoice with the new payment link
      if (paymentData?.url) {
        const { error: updateError } = await supabase
          .from('invoices')
          .update({
            payment_url: paymentData.url,
            payment_session_id: paymentData.id
          })
          .eq('id', invoiceId);

        if (updateError) {
          console.error('Error updating invoice with payment link:', updateError);
          throw updateError;
        }
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast.error(t("toasts.failed_to_create_payment_link"));
      throw error;
    }
  };

  // Update the useEffect to also consider initialStage.date changes
  useEffect(() => {
    if (mode === "edit" && existingInvoice) {
      // If the invoice has a payment_url, it means a payment link was created
      setCreatePaymentLink(Boolean(existingInvoice.payment_url));
    } else if (mode !== "edit") {
      // For new invoices, only check the box if Stripe is enabled AND there's an initial date
      setCreatePaymentLink(subscriptionInfo?.stripeEnabled && Boolean(initialStage.date));
    }
  }, [mode, existingInvoice, initialStage.date, subscriptionInfo?.stripeEnabled]);

  const shouldShowFullForm = mode !== "schedule";
  const shouldSchedule = Boolean(initialStage.date);

  useEffect(() => {
    if (initialScheduleExpanded && scheduleRef.current) {
      scheduleRef.current.scrollIntoView({ behavior: 'smooth' });
      setIsScheduleExpanded(true);
    }
  }, [initialScheduleExpanded]);

  const handleBulkScheduleSubmit = async () => {
    if (!initialStage.date || !initialStage.templateId) {
      toast.error(t("toasts.set_initial_date"));
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("toasts.login_to_schedule"));
        return;
      }

      // Check Stripe configuration if payment links are requested
      let stripeConfigured = false;
      if (createPaymentLink) {
        const { data: profile, error: profileError } = await supabase
          .from('business_profiles')
          .select('stripe_account_id, stripe_charges_enabled, stripe_details_submitted, sepa_enabled')
          .eq('user_id', user.id)
          .single();

        if (profileError || !profile) {
          console.error('Error fetching business profile:', profileError);
          toast.error(t("toasts.failed_create_payment_link"));
          return;
        }

        // Check if Stripe is properly configured
        if (!profile.stripe_account_id || !profile.stripe_charges_enabled) {
          toast.error(
            <div className="flex flex-col gap-2">
              <p className="text-sm font-thin">
                {t("toasts.failed_create_payment_link")}{" "}
                <a
                  href="/account_settings"
                  className="text-primary hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/account_settings");
                  }}
                >
                  {t("toasts.connect_stripe_account")}
                </a>
              </p>
            </div>
          );
          return;
        }

        stripeConfigured = true;
      }

      // Process each invoice ID in the array
      const results = await Promise.all(invoiceIds.map(async (invoiceId) => {
        try {
          const initialDateTime = combineDateTime(initialStage.date, initialStage.time);

          const { data: invoice, error: invoiceError } = await supabase
            .from('invoices')
            .select('subtotal, amount, bank_iban, recipient_email, recipients_emails, recipient_address, description')
            .eq('id', invoiceId)
            .single();

          if (invoiceError || !invoice) {
            console.error('Error fetching invoice:', invoiceError);
            return { success: false, id: invoiceId, error: 'Invoice not found' };
          }

          const emailsToUse = (invoice.recipients_emails && invoice.recipients_emails.length > 0)
            ? invoice.recipients_emails
            : recipientEmails.length > 0
              ? recipientEmails
              : [invoice.recipient_email];

          // Create payment link if requested and Stripe is configured
          let paymentUrl = null;
          let paymentSessionId = null;

          if (createPaymentLink && stripeConfigured) {
            try {
              // Create a payment link using the primary recipient email
              const { data: paymentData, error: paymentError } = await supabase.functions.invoke('create-checkout-link', {
                body: {
                  amount: invoice.amount,
                  invoice_id: invoiceId,
                  currency: 'eur',
                  recipient_email: invoice.recipient_email
                }
              });

              if (paymentError) {
                console.error('Error creating payment link for invoice', invoiceId, paymentError);
              } else if (paymentData?.url) {
                paymentUrl = paymentData.url;
                paymentSessionId = paymentData.id;
              }
            } catch (paymentError) {
              console.error('Error creating payment link for invoice', invoiceId, paymentError);
              // Continue with scheduling even if payment link creation fails
            }
          }

          // Update invoice with payment info (if available) and status
          const { error: updateError } = await supabase
            .from('invoices')
            .update({
              payment_url: paymentUrl,
              payment_session_id: paymentSessionId,
              status: 'scheduled',
              recipients_emails: emailsToUse
            })
            .eq('id', invoiceId);

          if (updateError) throw updateError;

          // Delete existing schedule if any
          const { error: deleteError } = await supabase
            .from('invoice_schedules')
            .delete()
            .eq('invoice_id', invoiceId);

          if (deleteError) console.error('Error deleting existing schedule:', deleteError);

          // Create new schedule
          const { error: scheduleError } = await supabase
            .from('invoice_schedules')
            .insert({
              invoice_id: invoiceId,
              user_id: user.id,
              recipient_email: emailsToUse.join(','),
              recipients_emails: emailsToUse,
              initial_date: initialDateTime.toISOString(),
              initial_template_id: initialStage.templateId,
              first_reminder_template_id: firstReminder.templateId,
              first_reminder_date: firstReminder.date ? combineDateTime(firstReminder.date, firstReminder.time).toISOString() : null,
              second_reminder_template_id: secondReminder.templateId,
              second_reminder_date: secondReminder.date ? combineDateTime(secondReminder.date, secondReminder.time).toISOString() : null,
              final_reminder_template_id: finalReminder.templateId,
              final_reminder_date: finalReminder.date ? combineDateTime(finalReminder.date, finalReminder.time).toISOString() : null,
            });

          if (scheduleError) {
            console.error('Error creating schedule:', scheduleError);
            return { success: false, id: invoiceId, error: scheduleError.message };
          }

          return {
            success: true,
            id: invoiceId,
            paymentCreated: Boolean(paymentUrl)
          };
        } catch (error) {
          console.error(`Error scheduling invoice ${invoiceId}:`, error);
          return { success: false, id: invoiceId, error: error.message };
        }
      }));

      const successCount = results.filter(r => r.success).length;
      const paymentLinksCreated = results.filter(r => r.success && r.paymentCreated).length;
      const failCount = results.length - successCount;

      if (failCount === 0) {
        if (createPaymentLink) {
          toast.success(t("toasts.bulk_schedule_with_payment_success", {
            count: successCount,
            payments: paymentLinksCreated
          }));
        } else {
          toast.success(t("toasts.bulk_schedule_success", { count: successCount }));
        }
      } else if (successCount === 0) {
        toast.error(t("toasts.bulk_schedule_all_failed"));
      } else {
        if (createPaymentLink) {
          toast.warning(t("toasts.bulk_schedule_partial_with_payment", {
            success: successCount,
            fail: failCount,
            payments: paymentLinksCreated
          }));
        } else {
          toast.warning(t("toasts.bulk_schedule_partial", {
            success: successCount,
            fail: failCount
          }));
        }
      }

      onSuccess?.();
    } catch (error: any) {
      console.error('Error bulk scheduling invoices:', error);
      toast.error(t("toasts.failed_schedule_invoice"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // For bulk scheduling, bypass the form validation
          if (mode === "schedule" && invoiceIds.length > 0) {
            handleBulkScheduleSubmit();
          } else {
            form.handleSubmit(handleFormSubmit)(e);
          }
        }}
        className="space-y-6 md:space-y-8"
      >
        {shouldShowFullForm && (
          <>
            <InvoiceHeader form={form} issueDate={issueDate} />
            <SellerInformation form={form} />
            <BuyerInformation form={form} />
            <InvoiceLineItems form={form} />
          </>
        )}

        <div className="border rounded-lg" ref={scheduleRef}>
          <button
            type="button"
            className="w-full p-3 md:p-4 flex justify-between items-center text-[#34375B] bg-[#BCC4F4] hover:bg-accent/50 transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setIsScheduleExpanded(!isScheduleExpanded)}
            disabled={subscriptionInfo && !subscriptionInfo.canSchedule}
          >
            <span className="font-medium flex items-center gap-2">
              <h3 className="text-sm md:text-base">{t("create_invoice_form.schedule_settings", "Schedule Settings")}</h3>
              {subscriptionInfo && !subscriptionInfo.canSchedule && (
                <Lock className="h-4 w-4" />
              )}
            </span>
            {isScheduleExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {isScheduleExpanded && (
            <div className="p-3 md:p-4 space-y-4 md:space-y-6">
              {subscriptionInfo && !subscriptionInfo.canSchedule ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    {t("toasts.usage_status", {
                      used: subscriptionInfo.used,
                      limit: subscriptionInfo.limit,
                      tier: subscriptionInfo.tier
                    })}
                    {t("toasts.limit_reset")}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {mode !== "schedule" && (
                    <div className="space-y-2">
                      <Label htmlFor="recipientEmails" className="text-[#34375B] text-sm md:text-base">
                        {t("schedule_dialog.recipient_emails")}
                      </Label>
                      <div className="flex bg-white flex-wrap gap-2 p-2 border rounded-md bg-background min-h-[40px]">
                        {recipientEmails.map((email) => (
                          <div
                            key={email}
                            className="flex items-center gap-1 px-2 py-1 bg-[#BCC4F4] text-[#34375B] rounded-full text-xs md:text-sm"
                          >
                            <span className="max-w-[150px] md:max-w-none truncate">{email}</span>
                            <button
                              type="button"
                              onClick={() => removeEmail(email)}
                              className="text-[#34375B] hover:text-[#5CC2BB] transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <Input
                          id="recipientEmails"
                          type="text"
                          value={currentEmail}
                          onChange={handleEmailInputChange}
                          onKeyDown={handleEmailKeyDown}
                          className="flex-1 border-none bg-white shadow-none focus-visible:ring-0 min-w-[120px] md:min-w-[200px] p-0 border border-gray-300 text-sm"
                        />
                      </div>
                      <p className="text-xs md:text-sm font-thin text-muted-foreground">
                        {t("schedule_dialog.enter_multiple_email")}
                      </p>
                    </div>
                  )}

                  <ReminderStageSection
                    title={t("schedule_dialog.initial_email")}
                    stage={initialStage}
                    templateType="initial"
                    onChange={setInitialStage}
                    onInitialDateChange={handleInitialDateChange}
                    useCurrentTimePlusFifteen={true}
                    allowClear={true}
                    onClear={() => setInitialStage(prev => ({ ...prev, date: undefined, time: initialStage.time, templateId: undefined }))}
                  />

                  <ReminderStageSection
                    title={t("schedule_dialog.first_reminder")}
                    stage={firstReminder}
                    templateType="reminder_1"
                    onChange={setFirstReminder}
                    previousStageDate={initialStage.date}
                    allowClear={true}
                    onClear={() => setFirstReminder(prev => ({ ...prev, date: undefined, time: '09:00', templateId: undefined }))}
                  />

                  <ReminderStageSection
                    title={t("schedule_dialog.second_reminder")}
                    stage={secondReminder}
                    templateType="reminder_2"
                    onChange={setSecondReminder}
                    previousStageDate={firstReminder.date}
                    allowClear={true}
                    onClear={() => setSecondReminder(prev => ({ ...prev, date: undefined, time: '09:00', templateId: undefined }))}
                  />

                  <ReminderStageSection
                    title={t("schedule_dialog.final_reminder")}
                    stage={finalReminder}
                    templateType="reminder_4"
                    onChange={setFinalReminder}
                    previousStageDate={secondReminder.date}
                    allowClear={true}
                    onClear={() => setFinalReminder(prev => ({ ...prev, date: undefined, time: '09:00', templateId: undefined }))}
                  />

                  {/* Place this just before the submit button div */}
                  <div className="flex justify-end w-full">
                    <div
                      className="inline-flex items-center p-2 md:p-3 border rounded-md relative group w-full sm:w-auto"
                      style={{
                        backgroundColor: subscriptionInfo?.stripeEnabled ? 'white' : '#f5f5f5',
                        opacity: subscriptionInfo?.stripeEnabled ? 1 : 0.7,
                        cursor: subscriptionInfo?.stripeEnabled ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <Label
                        htmlFor="createPaymentLink"
                        className={`text-xs md:text-sm font-medium text-[#34375B] mr-2 md:mr-3 ${!subscriptionInfo?.stripeEnabled ? 'text-muted-foreground' : ''}`}
                      >
                        {t("schedule_dialog.create_payment_link", "Create Stripe payment link")}
                      </Label>
                      <input
                        type="checkbox"
                        id="createPaymentLink"
                        checked={createPaymentLink}
                        onChange={(e) => setCreatePaymentLink(e.target.checked)}
                        disabled={!subscriptionInfo?.stripeEnabled}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />

                      {/* Helper text that appears on hover when Stripe is not enabled */}
                      {!subscriptionInfo?.stripeEnabled && (
                        <div className="absolute left-0 sm:right-full sm:left-auto mr-0 sm:mr-2 top-full sm:top-1/2 mt-2 sm:mt-0 sm:-translate-y-1/2 bg-white p-2 md:p-3 border rounded-md shadow-md opacity-0 group-hover:opacity-100 group-hover:text-[#34375B] transition-opacity z-10 text-xs md:text-sm text-muted-foreground w-full sm:w-80">
                          {t("schedule_dialog.stripe_not_configured")}
                          <a
                            href="/account_settings"
                            className="text-primary hover:underline cursor-pointer ml-1"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate("/account_settings");
                            }}
                          >
                            {t("schedule_dialog.stripe_setup_account_settings")}
                          </a>
                        </div>
                      )}

                      {/* New helper text for when Stripe is enabled but SEPA is not */}
                      {subscriptionInfo?.stripeEnabled && !subscriptionInfo?.sepaEnabled && (
                        <div className="absolute left-0 sm:right-full sm:left-auto mr-0 sm:mr-2 top-full sm:top-1/2 mt-2 sm:mt-0 sm:-translate-y-1/2 bg-white p-2 md:p-3 border rounded-md shadow-md opacity-0 group-hover:opacity-100 group-hover:text-[#34375B] transition-opacity z-10 text-xs md:text-sm text-muted-foreground w-full sm:w-80">
                          {t("schedule_dialog.sepa_not_enabled", "Enable SEPA Direct Debit in your Stripe account for lower transfer fees.")}
                          <a
                            href="/account_settings"
                            className="text-primary hover:underline cursor-pointer ml-1"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate("/account_settings");
                            }}
                          >
                            {t("schedule_dialog.go_to_account_settings", "Go to account settings")}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Submit button div */}
        <div className="flex justify-end gap-4">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button
            type="submit"
            size="lg"
            className="hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-transparent hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-[1px] hover:-translate-y-[1px] transition-all duration-200"
            disabled={isSubmitting || (shouldSchedule && subscriptionInfo && !subscriptionInfo.canSchedule)}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "edit"
              ? t("common.update")
              : mode === "schedule"
                ? invoiceIds.length > 1
                  ? t("schedule_dialog.schedule_invoices_button", { count: invoiceIds.length })
                  : t("schedule_dialog.schedule_invoice_button")
                : shouldSchedule
                  ? t("schedule_dialog.schedule_invoice_button")
                  : t("schedule_dialog.create_invoice_button")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
